import { LitElement, html, css, nothing } from "lit";
import { property, customElement } from "lit/decorators.js";
import "@maersk-global/mds-components-core/mc-modal";
import {
  Alarm,
  AlarmState,
  AlarmType,
} from "../../../src/services/types/fleet-alarms-service.types";
import { VesselsModel } from "../../../src/models/vessels-model";
import { AlarmEvents } from "../../../src/models/types/voy-fleet-alarm-model.types";
import { VoyFleetAlarmModel } from "../../../src/models/voy-fleet-alarm-model";
import {
  AlertId,
  VesselAlertsModel,
} from "../../../src/models/vessel-alerts-model";

@customElement("voy-weather-alarm-popup")
export class VoyWeatherAlarm extends LitElement {
  @property({ type: Boolean, reflect: true }) shown: boolean = true;
  @property({ type: Array }) alarms: Alarm[] = [];
  @property({ type: Object }) alarm: Alarm | null = null;
  @property({ attribute: false }) selected: string = "";

  static get styles() {
    return [
      css`
        :host {
          font-family: MaerskText;
        }
        .content {
          margin-left: 20px;
        }
        .max-width-container h2 {
          margin-top: 0px;
        }
        .desc {
          color: var(--neutrals-grey-02, grey);
          font-size: 14px;
          font-family: MaerskText;
          line-height: 20px;
          margin-top: -10px;
        }
        .desc span {
          text-decoration: underline;
          cursor: pointer;
        }
        .title {
          margin-top: 0px;
          font-family: MaerskText;
          font-size: 16px;
        }
        .alarm-description {
          font-size: 13px;
          font-family: MaerskText;
          font-weight: 400;
        }
        .max-width-container {
          width: 100%;
        }
        .footer {
          text-align: end;
          padding: 16px 24px;
          margin: -24px;
          margin-top: 20px;
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          align-items: center;
          padding: 16px 24px;
          gap: 12px;
          height: 64px;
          background: #f7f7f7;
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
        }
        .footer-button {
          background-color: #ffffff;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }); // Attach shadow DOM
    this._addAlertForAcceptedAlarm = this._addAlertForAcceptedAlarm.bind(this);
  }

  private _convertToFormattedDate(date: string) {
    const d = new Date(date);
    const month = d.toLocaleString("default", { month: "short" });
    const day = d.getDate();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${day} ${month} ${hours}:${minutes} UTC`;
  }

  private _onDoItLaterClick() {
    if (!this.alarm) {
      return;
    }

    
    if (this.alarm?.id) {
      const pendingAlarms = JSON.parse(
        localStorage.getItem("doItLaterAlarm") || "[]"
      );
      //adding the snoozedTime to alarm object - this is required to pull 1 hour old snoozed alarms
      const snoozedAlarm = { ...this.alarm, snoozedTime: new Date().toISOString() };
      localStorage.setItem(
        "doItLaterAlarm",
        JSON.stringify([...pendingAlarms, snoozedAlarm])
      );
    }
    this.alarms.shift();
    this.dispatchEvent(new Event("close"));
    this.dispatchEvent(
      new CustomEvent(AlarmEvents.CheckMoreAlarms, { detail: this.alarms })
    );

  }

  private async _onSubmit() {
    if (!this.alarm) {
      return;
    }
    try {
      const response  = await VoyFleetAlarmModel?.updateWeatherAlarm(
        this.alarm!.id,
        AlarmState.InProgress,
        "Alarm accepted"
      );
      if(response){
        const alertType =
        this.alarm?.alarmType === AlarmType.HeavyWeather
          ? AlertId.HeavyWeather
          : AlertId.ParametricRoll;
          VesselAlertsModel!.addAlert(this.alarm.imo, {
            id: `${alertType}-${this.alarm.imo}`,
            message: this._getMessaegForAlert(this.alarm),
        });
        }
      let pendingAlarm = JSON.parse(
        localStorage.getItem("doItLaterAlarm") || "[]"
      ) as Alarm[];
      const indexOfItem = pendingAlarm.findIndex(
        (a) => a.id === this.alarm?.id
      );
      if (indexOfItem >= 0)
        localStorage.setItem(
          "doItLaterAlarm",
          JSON.stringify([
            ...pendingAlarm.slice(0, indexOfItem),
            ...pendingAlarm.slice(indexOfItem + 1),
          ])
        );

      this.alarms.shift();
      this.dispatchEvent(new Event("close"));
      this.dispatchEvent(
        new CustomEvent(AlarmEvents.CheckMoreAlarms, { detail: this.alarms })
      );
    } catch (error:any) {
      console.log(error);

    }
  }

  private getDescription(alarm: Alarm) {
    const alarmName =
      alarm?.alarmType === AlarmType.HeavyWeather
        ? "Heavy weather"
        : "Parametric roll";
    const date = this._convertToFormattedDate(alarm?.startTs);
    if (alarmName && date) {
      return `The vessel is currently approaching a part of the route with increased risk of ${alarmName}, and the vessel will reach this part at around ${date}.`;
    }
  }

  private getHeading(alarm: Alarm) {
    const vesselName = VesselsModel!.getVesselName(alarm?.imo);
    const date = this._convertToFormattedDate(alarm?.startTs);
    const alarmName =
      alarm?.alarmType === AlarmType.HeavyWeather
        ? "Heavy weather"
        : "Parametric roll";
      return `${vesselName} (IMO: ${alarm?.imo}) with risk of ${alarmName} |  ${date}`;
  }

  connectedCallback()
  {
  super.connectedCallback();
  
  VoyFleetAlarmModel!.addEventListener(AlarmEvents.WeatherAlarmAcceptedByOtherVET,this._addAlertForAcceptedAlarm);
}
  render() {
    if (this.alarm) {
      return html`${this._renderWeatherAlarmModal(this.alarm)}`;
    }
    return nothing;
  }

  private _renderWeatherAlarmModal(alarm: Alarm) {
    return html`
      <mc-modal
        heading="${this.getHeading(alarm) ?? ''}"
        ?open=${this.shown}
        dimension="small"
        width="528px"
        height="225px"
        backdropcloseactiondisabled
        escapecloseactiondisabled
        entercloseactiondisabled
        hiddenclose
        @closing=${() => this.dispatchEvent(new Event("close"))}
      >
        ${this._renderModalContent(alarm)}
        <div class="footer">
          <mc-button
            focusstartanchor
            appearance="neutral"
            variant="filled"
            @click=${this._onDoItLaterClick}
            >Remind me later</mc-button
          >
          <mc-button
            focusstartanchor
            appearance="primary"
            variant="filled"
            @click=${this._onSubmit}
            >Accept</mc-button
          >
        </div>
      </mc-modal>
    `;
  }

  private _renderModalContent(alarm: Alarm) {
    return html`
      <div class="alarm-description">${this.getDescription(alarm) ?? ''}</div>
    `;
  }

  private _getMessaegForAlert(alarm: Alarm) {
    //TODO: Actual message drafted in alert-messages component. Handle this properly
    const alarmType = alarm?.alarmType === AlarmType.HeavyWeather ? "Heavy weather" : "Parametric roll";
    const vesselName = VesselsModel!.getVesselName(alarm?.imo);
    return `You have accepted the alert. View Details. 
      Notes can be added to activity log section. `;
  }

  private _addAlertForAcceptedAlarm(evt: Event) {
    //if alert is already accepted by other user, then add this alert to model
    const detail = (evt as CustomEvent).detail;
    const alertType =
        this.alarm?.alarmType === AlarmType.HeavyWeather
          ? AlertId.HeavyWeather
          : AlertId.ParametricRoll;
    const userName = detail.err.data?.userData?.displayName;
    VesselAlertsModel!.addAlert(this.alarm?.imo ||0, {
      id: `${alertType}-${this.alarm?.imo}-error`,
      message: `${userName}`,
  });
  }

}